// only the relevant parts shown—this is a full replacement for the component you pasted
import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import InputRadio from '@/components/ui/inputs/input-radio';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { useForm, Controller } from 'react-hook-form';
import { addItemVariationService, updateItemVariationService, getByIdItemVariationService } from '@/services/ItemVariationServices';
import { getItemAttributeService } from '@/services/ItemAttributeServices';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';
import CommonModal from '@/components/ui/modals/CommonModal';

const AddEditItemVariationModel = ({ modelTitle, openModel, setOpenModel, getTableData, itemId, editDataId = '' }) => {
  const selectRef = useRef(null);
  const {
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm();

  const dispatch = useDispatch();
  const [itemStatus, setItemStatus] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);
  const [attributDataSource, setAttributDataSource] = useState([]);

  useEffect(() => {
    if (openModel) {
      formReset();
      getAttributeData();
      if (editDataId) getDataById(editDataId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModel]);

  const formReset = async () => {
    try {
      const formData = getValues();
      for (const key in formData) await setValue(key, '');
      await reset();
      await setValue('status', '1');
      await setItemStatus(1);
    } catch (error) {
      console.error('Error in onReset:', error);
    }
  };

  const handleModelClose = () => {
    formReset();
    setOpenModel(false);
    document.body.style.overflowY = 'auto';
    getTableData?.();
  };

  const handleChangeRadio = (event) => {
    if (event.target.name === 'status') {
      setValue('status', event.target.value);
      setItemStatus(event.target.value);
    }
  };

  const getAttributeData = () => {
    setIsLoading(true);
    dispatch(getItemAttributeService({ search: '', page: 1, limit: 100 }))
      .then((res) => {
        const arr = res?.data?.data?.data || [];
        const options = arr.map((el) => ({ value: el?._id, label: el?.name }));
        setAttributDataSource(options);
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const getDataById = (editId) => {
    setIsLoading(true);
    dispatch(getByIdItemVariationService(editId))
      .then((res) => {
        const d = res?.data?.data || {};
        // Normalize selection/value types
        setValue('name', d.name ?? '');
        setValue('price', d.price ?? '');
        setValue('item_attribute_id', d.item_attribute_id ?? '');
        setValue('caution', d.caution ?? '');
        setValue('status', d.status ?? '1');
        setItemStatus(d.status ?? 1);
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const onSubmit = (data) => {
    setIsFormSumbmit(true);
    const payload = {
      ...data,
      item_id: itemId,
      price: Number(data.price ?? 0),
      status: Number(data.status ?? 1),
      // ensure we send just the id
      item_attribute_id: data.item_attribute_id,
    };

    const thunk = editDataId ? updateItemVariationService(editDataId, payload) : addItemVariationService(payload);

    dispatch(thunk)
      .then(() => {
        formReset();
        setIsFormSumbmit(false);
        handleModelClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSumbmit(false);
      });
  };

  return (
    <CommonModal modelTitle={modelTitle} openModel={openModel} handleModelClose={handleModelClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 20 }}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Name"
                type="text"
                labelStyleClassName="db-field-title required"
                errorData={{ minLength: '2', maxLength: '20' }}
                errorType={errors?.name?.type}
              />
            )}
          />

          <Controller
            control={control}
            rules={{ required: true, min: 0 }}
            name="price"
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Additional Price"
                type="number"
                step="0.01"
                labelStyleClassName="db-field-title required"
                errorData={{ min: '0' }}
                errorType={errors?.price?.type}
              />
            )}
          />

          {/* ✅ react-select: portal + correct onChange/value mapping */}
          <Controller
            control={control}
            rules={{ required: true }}
            name="item_attribute_id"
            render={({ field }) => (
              <InputSelect
                id="item_attribute_id"
                labelText="Attribute"
                labelStyleClassName="db-field-title required"
                ref={selectRef}
                options={attributDataSource}
                // show the option whose value equals the stored id
                value={attributDataSource.find((o) => o.value === field.value) || null}
                onChange={(opt) => setValue('item_attribute_id', opt?.value ?? '')}
                errorType={errors?.item_attribute_id?.type}
              />
            )}
          />

          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">
              Status
            </label>
            <div className="db-field-radio-group">
              <InputRadio
                id="active"
                name="status"
                labelText="Active"
                value="1"
                onChange={() => handleChangeRadio({ target: { name: 'status', value: 1 } })}
                checked={String(itemStatus) === '1'}
              />
              <InputRadio
                id="inactive"
                name="status"
                labelText="Inactive"
                value="2"
                onChange={() => handleChangeRadio({ target: { name: 'status', value: 2 } })}
                checked={String(itemStatus) === '2'}
              />
            </div>
          </div>

          <Controller
            control={control}
            rules={{ minLength: 5, maxLength: 200 }}
            name="caution"
            render={({ field }) => (
              <InputTextarea
                {...field}
                type="text"
                labelText="Caution"
                errorData={{ minLength: '5', maxLength: '200' }}
                errorType={errors?.caution?.type}
              />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="submit" className="db-btn py-2 text-white bg-primary" disabled={isFormSumbmit || isLoading}>
              <i className="lab lab-save" />
              <span>save</span>
              {isFormSumbmit && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </button>
            <button type="button" className="modal-btn-outline modal-close" onClick={handleModelClose}>
              <i className="lab lab-close" />
              <span>close</span>
            </button>
          </div>
        </div>
      </form>
    </CommonModal>
  );
};

export default AddEditItemVariationModel;
